import { ArtworkImage } from './ArtworkImage';
import { DarkManDebtBadge } from './DarkManDebtBadge';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Investigator, SkillBreakdown, UserAction } from '../api';
import type { InvestigatorDisplayProps } from './InvestigatorDisplay';
import { StackedItems } from './InvestigatorSheet';
import { Card } from './Card';
import { GameDialog } from './GameDialog';
import { SimpleMarkdown } from './SimpleMarkdown';
import { PersonalStoryCard } from './PersonalStory';
import { GateMarker } from './GateMarker';
import { Monsters, MonstersDetails } from './Monsters';
import { StatisticsCard } from './StatisticsCard';
import StatBreakdown from './StatBreakdown';
import './InvestigatorDigitalSheet.css';
import { getInvestigatorArtworkPath } from '../utils/investigatorArtwork';

export { getInvestigatorArtworkPath } from '../utils/investigatorArtwork';

const Portrait: React.FC<{ name: string }> = ({ name }) => {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [fullFailed, setFullFailed] = useState(false);
  return <div className="investigator-digital-portrait">
    {failed ? <span className="investigator-digital-initial" aria-label={`${name} artwork unavailable`}>{name[0]}</span> : <>
      <img src={getInvestigatorArtworkPath(name)} alt={`${name} artwork`} width={200} height={200}
        loading="lazy" decoding="async" onError={() => { setFailed(true); setOpen(false); }} />
      <button type="button" className="investigator-artwork-open" aria-label={`View full artwork for ${name}`}
        aria-haspopup="dialog" onClick={event => { event.stopPropagation(); setFullFailed(false); setOpen(true); }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="10" cy="10" r="6" /><path d="m14.5 14.5 6 6" />
        </svg>
      </button>
    </>}
    {open && <GameDialog title={`${name} artwork`} variant="artwork" onClose={() => setOpen(false)}>
      {fullFailed ? <p role="alert">Artwork could not be loaded.</p> : <img className="investigator-artwork-full"
        src={getInvestigatorArtworkPath(name, false)} alt={`${name} full artwork`} width={1200} height={1200}
        decoding="async" onError={() => setFullFailed(true)} />}
    </GameDialog>}
  </div>;
};

const InvestigatorStatisticsPopup: React.FC<{ investigator: Investigator }> = ({ investigator }) => {
  const [open, setOpen] = useState(false);
  const popupId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!buttonRef.current?.contains(event.target as Node) && !popupRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', outside);
    // Handle Escape before the surrounding Inspector dialog's document listener.
    window.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('keydown', escape, true);
    };
  }, [open]);

  return <>
    <button ref={buttonRef} type="button" className="investigator-digital-statistics-button"
      aria-label="Show investigator statistics" title="Investigator statistics" aria-haspopup="dialog"
      aria-expanded={open} aria-controls={open ? popupId : undefined} onClick={() => setOpen(value => !value)}>
      <span aria-hidden="true">ⓘ</span>
    </button>
    {open && <div ref={popupRef} id={popupId} className="investigator-digital-statistics-popup" role="dialog"
      aria-label={`${investigator.name} statistics`} tabIndex={0}>
      <StatisticsCard investigator={investigator} />
    </div>}
  </>;
};

const Stat: React.FC<{
  label: string; value: React.ReactNode; breakdown?: SkillBreakdown; modifier?: string; note?: string; background?: React.ReactNode; healthLabel?: string; requireModifiers?: boolean;
}> = ({ label, value, breakdown, modifier, note, background, healthLabel, requireModifiers = false }) => {
  const color = !breakdown || breakdown.total === breakdown.base ? undefined
    : breakdown.total > breakdown.base ? 'var(--investigator-stat-positive)' : 'var(--investigator-stat-negative)';
  const hasBreakdown = !!(breakdown && (breakdown.modifiers?.length || (!requireModifiers && breakdown.baseSkillName)));
  const contents = <span className="investigator-digital-stat-content">{background}{!healthLabel && <span className="investigator-digital-stat-label">{label}</span>}
    {healthLabel ? <span className="investigator-digital-health-value"><strong style={{ color }}>{value}</strong>
      {hasBreakdown && <span className="investigator-digital-health-info" aria-hidden="true">ⓘ</span>}
    </span> : modifier ? <span className="investigator-digital-modified-value"><small>{modifier}</small><strong style={{ color }}>{value}</strong></span>
      : <strong style={{ color }}>{value}</strong>}{note && <small>{note}</small>}</span>;
  return hasBreakdown && breakdown
    ? <details className={`investigator-digital-stat investigator-digital-breakdown${healthLabel ? ' investigator-digital-health' : ''}`}>
      <summary aria-label={`${healthLabel ?? label}: ${typeof value === 'number' || typeof value === 'string' ? value : ''}. Show modifiers`}>{contents}</summary>
      <div className="investigator-digital-modifiers">
        {breakdown.baseSkillName && <p>Base skill: {breakdown.baseSkillName}</p>}
        <StatBreakdown breakdown={breakdown} />
      </div>
    </details>
    : <div className={`investigator-digital-stat${healthLabel ? ' investigator-digital-health' : ''}`}>{contents}</div>;
};

type SkillName = 'speed' | 'sneak' | 'fight' | 'will' | 'lore' | 'luck';
type SkillKey = SkillName | 'maxSanity' | 'maxStamina';
type Pair = { first: SkillKey; second: SkillKey; action: string; label: string };
const pairs: Pair[] = [
  { first: 'speed', second: 'sneak', action: 'speed', label: 'Speed / Sneak' },
  { first: 'fight', second: 'will', action: 'fight', label: 'Fight / Will' },
  { first: 'lore', second: 'luck', action: 'lore', label: 'Lore / Luck' },
];
const statLabel = (key: string) => key === 'maxSanity' ? 'Sanity' : key === 'maxStamina' ? 'Stamina' : key[0].toUpperCase() + key.slice(1);
const checkNames = ['evade', 'combat', 'horror', 'spell'] as const;
const defaultBases = { evade: 'sneak', combat: 'fight', horror: 'will', spell: 'lore' };

const TokenBackground: React.FC<{ kind: 'sanity' | 'stamina' | 'clue' | 'money'; count: number; filled?: number; extra?: number }> = ({ kind, count, filled = count, extra = 0 }) => {
  const total = Math.max(0, count + extra);
  const source = kind === 'clue' ? '/Clue.jpg' : kind === 'money' ? '/money.jpg' : '/' + kind + '.png';
  return <span className="investigator-digital-token-background" aria-hidden="true" data-token-kind={kind}>
    <span className="investigator-digital-token-stack" style={{ width: Math.max(0, total - 1) * 14 }}>
      {Array.from({ length: total }, (_, index) => <ArtworkImage key={index} src={source} alt=""
        className={['investigator-digital-stat-token', kind, index >= count ? 'extra-token' : index >= filled ? 'missing-token' : ''].join(' ')}
        style={{ left: total > 1 ? (index / (total - 1)) * 100 + '%' : '50%' }} />)}
    </span>
  </span>;
};

const SkillPair: React.FC<{
  pair: Pair; investigator: Investigator; reference: boolean; actions: UserAction[]; onExecuteAction?: (id: string) => void;
}> = ({ pair, investigator, reference, actions, onExecuteAction }) => {
  const maximumPair = pair.first === 'maxSanity';
  const min = maximumPair ? 4 : investigator[`${pair.first as SkillName}Min`];
  const selected = (maximumPair ? investigator.baseMaxSanity ?? investigator.maxSanity : investigator[pair.first]) - min;
  const previews = reference ? [] : investigator.skillSliderPreviews?.find(preview => preview.pair === pair.action)?.positions ?? [];
  const actionByPosition = new Map(actions.filter(action => action.id.startsWith('set_' + pair.action + '_')
    && action.sliderPosition != null).map(action => [action.sliderPosition, action]));
  const derivedFor = (key: SkillKey) => reference ? [] : [
    ...(key === 'speed' ? ['movement'] : []),
    ...checkNames.filter(check => {
      const currentBase = investigator[(check + 'Breakdown') as 'evadeBreakdown']?.baseSkillName?.toLowerCase() ?? defaultBases[check];
      return currentBase === key || previews.some(preview => preview.checks[check]?.baseSkillName?.toLowerCase() === key);
    }),
  ];
  const currentBreakdown = (check: string) => check === 'movement' ? investigator.movementPointsBreakdown
    : investigator[(check + 'Breakdown') as 'evadeBreakdown'];
  const label = (key: SkillKey) => {
    const breakdown = investigator[(key + 'Breakdown') as 'speedBreakdown'];
    const difference = breakdown ? breakdown.total - breakdown.base : 0;
    return <span className="investigator-digital-skill-half">
      {reference ? <span>{statLabel(key)}</span> : <Stat label={statLabel(key)} value={breakdown?.total ?? investigator[key]}
        breakdown={breakdown} modifier={difference ? (difference > 0 ? '+' : '') + difference : undefined} />}
      {derivedFor(key).map(check => <span key={check} className="investigator-digital-derived-label">
        <Stat label={check === 'movement' ? 'Movement' : statLabel(check)}
          value={check === 'movement' ? `${investigator.movementPoints}/${currentBreakdown(check)?.total ?? '—'}` : currentBreakdown(check)?.total ?? '—'}
          breakdown={currentBreakdown(check)} requireModifiers
          note={check === 'movement' && investigator.canNoLongerMoveThisTurn ? 'cannot move further' : undefined} />
      </span>)}
    </span>;
  };
  const values = (key: SkillKey, bottom: boolean, position: number) => {
    const preview = previews.find(candidate => candidate.sliderPosition === position);
    const breakdown = bottom ? preview?.second : preview?.first;
    const max = maximumPair ? 7 : investigator[`${key as SkillName}Max`];
    const base = breakdown?.base ?? Math.max(0, bottom && (!investigator.skillSlidersIncreaseTogether || maximumPair) ? max - position : max - 3 + position);
    return <span className={'investigator-digital-skill-half ' + (bottom ? 'red' : 'blue')}>
      <span className="investigator-digital-base-value" title={breakdown ? 'Base ' + base + '; modified ' + breakdown.total : undefined}>{base}</span>
      {derivedFor(key).map(check => {
        const result = check === 'movement' ? preview?.movementPointsBreakdown : preview?.checks[check];
        const applies = check === 'movement' || result?.baseSkillName?.toLowerCase() === key;
        const fallback = !preview && position === selected ? currentBreakdown(check)?.total : undefined;
        const value = result && applies ? result.total : fallback ?? '—';
        return <span key={check} className="investigator-digital-check-value" aria-label={(check === 'movement' ? 'Movement allowance' : statLabel(check)) + ': ' + value}>{value}</span>;
      })}
    </span>;
  };
  return <div className="investigator-digital-skill-pair" role="group" aria-label={pair.label}>
    <div className="investigator-digital-skill-label">{label(pair.first)}{label(pair.second)}</div>
    {Array.from({ length: 4 }, (_, position) => {
      const action = actionByPosition.get(position);
      const current = !reference && selected === position;
      const className = 'investigator-digital-skill-column' + (current ? ' selected' : '');
      const content = <>{values(pair.first, false, position)}{values(pair.second, true, position)}</>;
      return !reference && action && onExecuteAction ? <button key={position} type="button" className={className}
        data-game-action={action.id}
        disabled={!!action.disabled} aria-label={action.name || 'Set ' + pair.label + ' position ' + (position + 1)}
        title={action.description || action.name} data-position={position} aria-current={current ? 'true' : undefined}
        onClick={event => { event.stopPropagation(); if (!action.disabled) onExecuteAction(action.id); }}>{content}</button>
        : <div key={position} className={className} data-position={position} aria-current={current ? 'true' : undefined}>{content}</div>;
    })}
  </div>;
};

/** Shared by full sheets and the experimental Upkeep action panel. */
export const InvestigatorDigitalSkills: React.FC<Pick<InvestigatorDisplayProps,
  'investigator' | 'variant' | 'gameState' | 'availableActions' | 'onExecuteAction'>> = ({
  investigator: inv, variant = 'game', gameState, availableActions = [], onExecuteAction,
}) => {
  const reference = variant === 'reference';
  const ownActions = availableActions.filter(action => action.investigatorName?.toLowerCase() === inv.name.toLowerCase());
  const skillPairs = inv.name === 'Lily Chen' ? [...pairs,
    { first: 'maxSanity', second: 'maxStamina', action: 'max_sanity', label: 'Sanity / Stamina' } as Pair] : pairs;
  const upkeep = /^(Upkeep|Final Battle Upkeep)$/i.test(gameState?.currentPhase ?? '');
  const effectiveFocus = reference ? inv.baseFocus ?? inv.focus : inv.focusBreakdown?.total ?? inv.focus;
  return <>
    <div className="investigator-digital-focus"><Stat label="Focus" value={!reference && upkeep ? inv.focusRemaining + ' / ' + effectiveFocus : effectiveFocus}
      breakdown={reference ? undefined : inv.focusBreakdown} /></div>
    <section className="investigator-digital-block investigator-digital-skills" aria-label="Skills">
      {skillPairs.map(pair => <SkillPair key={pair.first} pair={pair} investigator={inv} reference={reference}
        actions={ownActions} onExecuteAction={onExecuteAction} />)}
    </section>
  </>;
};

export const InvestigatorDigitalSheet: React.FC<InvestigatorDisplayProps> = ({
  investigator: inv, variant = 'game', selectionPreview = false, gameState, availableActions = [], onExecuteAction, currentLocationName, deckInfo, language,
}) => {
  const reference = variant === 'reference';
  const same = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase();
  const owner = gameState?.players?.find(player => player.controlledInvestigators?.some(name => same(name, inv.name)));
  const multiplayer = (gameState?.expectedPlayersCount ?? 1) > 1;
  const online = gameState?.playersDetailed?.find(player => same(player.name, owner?.name))?.isOnline;
  const first = gameState?.firstInvestigator ? same(gameState.firstInvestigator, inv.name) : same(gameState?.firstPlayer, owner?.name);
  const location = currentLocationName ?? gameState?.locations?.find(place => place.investigatorNames?.some(name => same(name, inv.name)))?.name;
  const conditions = [
    inv.delayed ? 'Delayed' : '', location === 'Jail Cell' ? 'Arrested' : '', inv.explored ? 'Explored' : '',
    inv.isDevoured ? inv.requiresReplacementChoiceOnUpkeep ? 'Retired' : 'Devoured' : '',
    inv.hasBastToken ? 'Bast token' : '', inv.eihortBroodTokens ? `Eihort brood: ${inv.eihortBroodTokens}` : '',
    ...(inv.specialCards ?? []).filter(card => /bless|curse|deputy|loan/i.test(card.name)).map(card => card.title || card.name),
  ].filter(Boolean);
  const story = inv.personalStory;
  const hasCards = !!(story || inv.relationship || inv.partnerRelationship || [inv.commonItems, inv.uniqueItems, inv.spells, inv.skills, inv.allies, inv.specialCards, inv.traumas, inv.madnesses, inv.corruptions].some(cards => cards?.length));
  const maxima = (kind: 'Sanity' | 'Stamina') => reference ? inv[`baseMax${kind}`] ?? inv[`max${kind}`] : inv[`max${kind}Breakdown`]?.total ?? inv[`max${kind}`];
  const health = (kind: 'sanity' | 'stamina', max: number) => `${inv[kind]} / ${max}`;
  const extra = (kind: 'sanity' | 'stamina') => inv[`${kind}AboveMaximum`] > 0 ? `+${inv[`${kind}AboveMaximum`]} above maximum` : undefined;
  return <article className={`investigator-digital investigator-digital-${variant}${reference && selectionPreview ? ' investigator-digital-selection-preview' : ''}`} aria-label={`${inv.name} investigator ${reference ? 'reference' : 'sheet'}`} data-has-cards={!reference && hasCards ? 'true' : undefined}>
    <div className="investigator-digital-main">
    <header className="investigator-digital-header">
      {!reference && inv.statistics && <InvestigatorStatisticsPopup key={inv.name} investigator={inv} />}
      <h3>{inv.name}</h3>
      {!reference && <div className="investigator-digital-owner">
        {multiplayer && owner && <><span>({owner.name})</span><span className="online-status" role="img"
          aria-label={`${owner.name}: ${online ? 'Online' : 'Offline'}`} title={`${owner.name}: ${online ? 'Online' : 'Offline'}`} style={{ background: online ? 'green' : 'red' }} /></>}
        {first && <span className="first-player-badge">First Player</span>}
      </div>}
    </header>
    <div className="investigator-digital-overview">
    <div className="investigator-digital-resources">
      <Stat label="" healthLabel="Sanity" value={reference ? maxima('Sanity') : health('sanity', maxima('Sanity'))}
        breakdown={reference ? undefined : inv.maxSanityBreakdown} note={reference ? undefined : extra('sanity')} background={<TokenBackground kind="sanity" count={maxima('Sanity')} filled={reference ? maxima('Sanity') : inv.sanity} extra={reference ? 0 : inv.sanityAboveMaximum} />} />
      <Stat label="" healthLabel="Stamina" value={reference ? maxima('Stamina') : health('stamina', maxima('Stamina'))}
        breakdown={reference ? undefined : inv.maxStaminaBreakdown} note={reference ? undefined : extra('stamina')} background={<TokenBackground kind="stamina" count={maxima('Stamina')} filled={reference ? maxima('Stamina') : inv.stamina} extra={reference ? 0 : inv.staminaAboveMaximum} />} />
      {!reference && <><Stat label="" value={inv.maxClueTokens != null ? `${inv.clues} / ${inv.maxClueTokens}` : inv.clues} background={<TokenBackground kind="clue" count={Math.max(1, Math.min(5, inv.clues))} />} /><Stat label="" value={'$' + inv.money} background={<TokenBackground kind="money" count={1} />} /></>}
    </div>
    {!reference && (conditions.length > 0 || inv.hasDarkManDebt) && <ul className="investigator-digital-statuses" aria-label="Status">
      {inv.hasDarkManDebt && <li><DarkManDebtBadge /></li>}
      {conditions.map((condition, index) => <li key={`${condition}-${index}`}>
        {condition === 'Bast token' && <ArtworkImage src="/Guardians/BastToken.jpg" alt="" width={24} height={24} />}
        {condition.startsWith('Eihort brood:') && <ArtworkImage src="/AncientOnes/EihortToken.jpg" alt="" width={24} height={24} />}
        {condition}</li>)}
    </ul>}
      <Portrait key={inv.name} name={inv.name} />
      <section className="investigator-digital-block investigator-digital-abilities" aria-label="Abilities">
        <SimpleMarkdown text={inv.abilityDescription || ''} keyPrefix={`${inv.name}-digital-abilities`} />
      </section>
    </div>
    {reference && !selectionPreview && <section className="investigator-digital-block investigator-digital-possessions" aria-label="Starting possessions">
      <h4>Home</h4><p>{inv.startingLocation}</p>
      <h4>Fixed possessions</h4><p>{inv.fixedPossessionsDescription || 'None'}</p>
      <h4>Random possessions</h4><p>{inv.randomPossessionsDescription || 'None'}</p>
    </section>}
    {!(reference && selectionPreview) && <InvestigatorDigitalSkills investigator={inv} variant={variant} gameState={gameState}
      availableActions={availableActions} onExecuteAction={onExecuteAction} />}
    {!reference && <>
      {(deckInfo?.commonItems?.bottomCard || deckInfo?.uniqueItems?.bottomCard || deckInfo?.spells?.bottomCard) && <details className="investigator-digital-block">
        <summary>Revealed bottom cards</summary><div className="investigator-digital-bottom-cards">
          {deckInfo.commonItems?.bottomCard && <Card card={{ item: deckInfo.commonItems.bottomCard, kind: 'common' }} />}
          {deckInfo.uniqueItems?.bottomCard && <Card card={{ item: deckInfo.uniqueItems.bottomCard, kind: 'unique' }} />}
          {deckInfo.spells?.bottomCard && <Card card={{ item: deckInfo.spells.bottomCard, kind: 'spell' }} />}
        </div>
      </details>}
      {(!!inv.monsterTrophies?.length || !!inv.gateTrophies?.length) && <section className="investigator-digital-block investigator-digital-trophies" aria-label="Trophies">
        <h4>Trophies</h4><div className="investigator-digital-gates">{inv.gateTrophies?.map((gate, index) => <GateMarker key={index} gate={gate} language={language} onClickStopPropagation />)}</div>
        <div className="investigator-digital-monster-details"><MonstersDetails monsters={inv.monsterTrophies || []} /></div>
        <Monsters monsters={inv.monsterTrophies || []} wrapperClass="investigator-digital-monster-markers" tooltipPosition="left" />
      </section>}
    </>}
    {reference && !selectionPreview && story && <section className="investigator-digital-block" aria-label="Personal Story"><h4>Personal Story</h4>
      <div className="investigator-digital-stories"><PersonalStoryCard card={story} hideTooltip />
        {story.rewardName && story.rewardDescription && <PersonalStoryCard card={{ name: story.rewardName, description: story.rewardDescription, isReward: true }} />}
        {story.failName && story.failDescription && <PersonalStoryCard card={{ name: story.failName, description: story.failDescription, isReward: false }} />}
      </div>
    </section>}
    </div>
    {!reference && hasCards && <section className="investigator-digital-block investigator-digital-inventory" aria-label="Cards"><h4>Cards</h4>
        <div className="stacked-items">
          <StackedItems title="Personal" items={[]} kind="personalStory" entries={[
            ...(story ? [{ item: story, kind: 'personalStory' as const }] : []),
            ...[inv.relationship, inv.partnerRelationship].filter((card): card is NonNullable<typeof card> => !!card)
              .map(item => ({ item, kind: 'relationship' as const })),
          ]} />
          <StackedItems title="Common" items={inv.commonItems || []} kind="common" investigatorName={inv.name} />
          <StackedItems title="Unique" items={inv.uniqueItems || []} kind="unique" investigatorName={inv.name} />
          <StackedItems title="Spells" items={inv.spells || []} kind="spell" investigatorName={inv.name} />
          <StackedItems title="Skills" items={inv.skills || []} kind="skill" />
          <StackedItems title="Allies" items={inv.allies || []} kind="ally" />
          <StackedItems title="Special" items={inv.specialCards || []} kind="special" />
          <StackedItems title="Traumas" items={[]} kind="injury" entries={[
            ...(inv.traumas || []).map(item => ({ item, kind: 'injury' as const })),
            ...(inv.madnesses || []).map(item => ({ item, kind: 'madness' as const })),
          ]} />
          <StackedItems title="Corruptions" items={inv.corruptions || []} kind="corruption" />
        </div>
      </section>}
  </article>;
};
